/**
 * Phase 3.25 — B2C ad slot showing dealers who opted in to
 * marketing_b2c_ads_enabled. Used on the listings search/index page and on
 * each ListingDetailPage. Clicks go through /api/marketing/t/<code> which
 * sets a 30-day attribution cookie and redirects home.
 */
import { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";

// NOTE: ad.track_url returned by /api/marketing/ads/active already starts with
// "/api/...". So we must NOT add another "/api" suffix here, otherwise the
// link becomes /api/api/marketing/t/... and 404s the tracking redirect.
const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Feb-2026 (User-Wunsch 26 May): Sponsoren-/Preisvergleichs-Anzeigen nur an
// Besucher aus DE/AT/CH (DACH) ausspielen. Detektion bei eingeloggten Nutzern
// via `user.country`/`user.dealer_country`/`user.locale`; sonst via Browser-
// `navigator.language` (z.B. „de-DE", „de-AT", „de-CH").
const DACH_COUNTRIES = new Set(["DE", "AT", "CH"]);
const detectIsDach = (user) => {
    const fromUser = (user?.country || user?.dealer_country || user?.region || "").toUpperCase().slice(0, 2);
    if (fromUser) return DACH_COUNTRIES.has(fromUser);
    if (typeof navigator !== "undefined" && navigator.language) {
        const m = /[-_]([a-zA-Z]{2})$/.exec(navigator.language);
        const lang = (navigator.language || "").slice(0, 2).toLowerCase();
        const region = (m ? m[1] : "").toUpperCase();
        if (region) return DACH_COUNTRIES.has(region);
        // Fallback: nur Sprache "de" → wahrscheinlich DACH-User.
        return lang === "de";
    }
    return false;
};

export default function SponsoredAdSlot({ variant = "inline", limit = 3 }) {
    const [ads, setAds] = useState([]);
    const { user } = useAuth();
    const isDach = detectIsDach(user);

    useEffect(() => {
        if (!isDach) {
            setAds([]);
            return undefined;
        }
        let alive = true;
        api.get(`/marketing/ads/active?limit=${limit}`)
            .then((r) => { if (alive) setAds(r.data?.ads || []); })
            .catch(() => { if (alive) setAds([]); });
        return () => { alive = false; };
    }, [limit, isDach]);

    if (!isDach || !ads.length) return null;

    return (
        <div className={variant === "card" ? "space-y-2" : "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3"} data-testid="sponsored-ad-slot">
            {ads.map((ad) => {
                const trackUrl = `${API_BASE}${ad.track_url}`;
                return (
                    <a
                        key={ad.dealer_id}
                        href={trackUrl}
                        target="_blank"
                        rel="noopener noreferrer sponsored"
                        className="block bg-[#111827] hover:bg-[#1F2937] text-white rounded-sm overflow-hidden border-l-4 border-[#FFCC00] transition-colors"
                        data-testid={`sponsored-ad-${ad.dealer_id}`}
                    >
                        <div className="p-4 flex items-center gap-3">
                            {ad.logo_url && (
                                <img src={ad.logo_url} alt="" className="w-12 h-12 rounded-sm bg-white object-contain shrink-0" />
                            )}
                            <div className="flex-1 min-w-0">
                                <div className="text-[9px] uppercase tracking-widest font-bold text-[#FFCC00]">Werbung · Anzeige</div>
                                <div className="font-bold text-sm truncate" data-testid={`sponsored-ad-${ad.dealer_id}-headline`}>{ad.headline}</div>
                                <div className="text-xs text-gray-300 truncate">{ad.subline}{ad.city ? ` · ${ad.city}` : ""}</div>
                            </div>
                            <span className="hidden sm:inline-flex px-3 py-1.5 bg-[#FFCC00] text-[#111827] rounded-sm text-[10px] font-black uppercase tracking-wider whitespace-nowrap">Ansehen</span>
                        </div>
                    </a>
                );
            })}
        </div>
    );
}
